import {zodResolver} from '@hookform/resolvers/zod';
import {Button, Stack, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';

import {AlertInfo, MaskedInput} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {translations} from 'presentation/translations-new';
import {formatMasks, masks} from 'presentation/utils';
import {useChangeDataFormStateStore} from 'presentation/pages/private/profile/store/changeDataFormStateStore';
import {DiscardChangesModal} from '../DiscardChangesModal';
import {ContactFormProps} from './types';
import {CellPhoneFormValues, CellPhoneSchema} from './validation';
import {validateCellPhone} from 'presentation/utils/validateCellPhone';
import {usePersonalData} from 'presentation/hooks';
import {useChangeDataAlertStore} from 'presentation/pages/private/profile/store/changeDataAlertStore';
import {AlertEnum} from 'presentation/pages/private/profile/store/changeDataAlertStore/types';
import Icon from 'presentation/icons';
import IconNamesType from 'presentation/icons/IconNamesType';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';

const {cancel, save, requiredField} = translations['pt-br'].commons;
const {contact, successAlert, errorAlert} = translations['pt-br'].privateFlow.profile.changeData;

const getSuccessMessage = (phone?: string) =>
	phone
		? `Seu novo número ${formatMasks('phone', phone)} foi salvo com sucesso. Usaremos ele para enviar avisos do seu plano.`
		: successAlert.account;

const CellPhoneForm = ({
	onCancel,
	onSave,
	resetAbandonError,
	abandonError,
	loading,
}: ContactFormProps) => {
	const [isDiscardChangesModalOpenState, setIsDiscardChangesModalOpenState] = useState(false);
	const setChangeDataFormIsDirtyState = useChangeDataFormStateStore(
		state => state.updateFormState,
	);
	const {currentPhone} = usePersonalData();
	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const methods = useForm<CellPhoneFormValues>({
		resolver: zodResolver(CellPhoneSchema),
		mode: 'onSubmit',
		defaultValues: {
			cellphone: isNewProfileFeatureFlagEnabled && currentPhone ? currentPhone : '',
		},
	});

	const {
		register,
		formState: {errors, isDirty},
		handleSubmit,
		watch,
		setError,
		setValue,
	} = methods;

	const changeDataAlertState = useChangeDataAlertStore(state => state.status);

	const currentPhoneIsInvalid = !validateCellPhone(currentPhone);
	const showAlert =
		changeDataAlertState.status && changeDataAlertState.type === AlertEnum.CELLPHONE;
	const {typeMessage} = changeDataAlertState;
	const message = {
		success: getSuccessMessage(currentPhone),
		error: errorAlert,
	};
	const icon = {
		success: 'check-circle-outline-filled',
		error: 'error-outline',
	};
	const handleBack = () => {
		if (!isNewProfileFeatureFlagEnabled && isDirty) {
			setIsDiscardChangesModalOpenState(true);
			return;
		}

		onCancel();
	};

	useEffect(() => {
		setChangeDataFormIsDirtyState({
			isFormDirty: isDirty,
		});
	}, [isDirty]);

	const onSubmit = (data: CellPhoneFormValues) => {
		const normalizedCellphone = data.cellphone.replace(/\D/g, '');
		const normalizedCurrentPhone = currentPhone?.replace(/\D/g, '');

		if (normalizedCellphone === normalizedCurrentPhone) {
			setError('cellphone', {
				type: 'manual',
				message: contact.sameCellphone,
			});
			return;
		}

		onSave(data.cellphone);
	};

	const handleSave = () => {
		const normalizedCellphone = watch('cellphone').replace(/\D/g, '');
		const normalizedCurrentPhone = currentPhone?.replace(/\D/g, '');

		if (normalizedCellphone === normalizedCurrentPhone) {
			setError('cellphone', {
				type: 'manual',
				message: contact.sameCellphone,
			});
			return;
		}

		handleSubmit(onSubmit)();
	};

	const handleHelperCellPhoneText = () => {
		const {cellphone} = watch();
		if (errors.cellphone?.message) {
			if (cellphone.length === 0) {
				return requiredField;
			}
			return errors.cellphone.message;
		}
		if (abandonError) {
			return contact.cellphoneAbandonError;
		}
		return undefined;
	};

	if (abandonError) {
		setTimeout(() => {
			resetAbandonError();
		}, 3000);
	}

	return (
		<FormProvider {...methods}>
			<Stack spacing={2} mt={!isNewProfileFeatureFlagEnabled ? 2 : undefined}>
				{isNewProfileFeatureFlagEnabled && currentPhoneIsInvalid ? (
					<AlertInfo.Root severity="warning">
						<AlertInfo.Description>{contact.invalidPhone}</AlertInfo.Description>
					</AlertInfo.Root>
				) : null}
				{isNewProfileFeatureFlagEnabled && showAlert ? (
					<AlertInfo.Root
						severity={typeMessage}
						icon={<Icon name={icon[typeMessage] as IconNamesType} />}>
						<Typography fontSize={'1rem'} fontWeight={600}>
							{message[typeMessage]}
						</Typography>
					</AlertInfo.Root>
				) : null}
				<Stack spacing={2} flex={1}>
					<MaskedInput
						{...register('cellphone')}
						id="cellphone"
						inputMode="tel"
						error={!!errors.cellphone || abandonError}
						helperText={handleHelperCellPhoneText()}
						fullWidth
						autoComplete="current-cellphone"
						label={contact.cellphone}
						placeholder={contact.cellphonePlaceholder}
						$fieldName="cellphone"
						maskOptions={masks.cellphone}
						handleClearQuery={() => setValue('cellphone', '')}
					/>
				</Stack>

				<Stack direction="row" flex={1} justifyContent="flex-end" spacing={2}>
					<Button size="large" variant="outlined" onClick={handleBack}>
						{cancel}
					</Button>
					<Button disabled={loading} size="large" onClick={handleSave}>
						<Stack gap={1} direction="row">
							{save}
							{loading ? <Loading size={18} color="inherit" /> : null}
						</Stack>
					</Button>
				</Stack>
			</Stack>

			<DiscardChangesModal
				open={isDiscardChangesModalOpenState}
				setOpen={setIsDiscardChangesModalOpenState}
				onConfirm={onCancel}
			/>
		</FormProvider>
	);
};

export {CellPhoneForm};
